<template>
    <v-app>
        <component :is="layout" class="stretch" style="height: calc(100% - 20px)">
            <router-view :key="$route.fullPath"></router-view>
        </component>
    </v-app>
</template>

<script>
import {VueController} from './service/support/vuex-controller';
import {EventBus} from './service/support/event-bus';
import Auth from '@aws-amplify/auth';

export default VueController({
    watch: {
        '$route' (to, from) {
            document.title = to.meta.title ? ("Zapa: " + to.meta.title) : 'Zapa File Portals';
        },
        organization: {
            handler(newOrg) {
                if (newOrg && newOrg.primaryBrandColor) {
                    this.applyBrandColor(newOrg.primaryBrandColor);
                }
            },
            deep: true,
            immediate: true
        }
    },
    state: [
        "cognito",
        "isAuthenticated",
        "user",
        "organization"
    ],
    data() {
        return {
            newAccount: true
        }
    },
    methods: {
        hexToRgb(hex) {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : null;
        },
        rgbToHsl(r, g, b) {
            r = r / 255;
            g = g / 255;
            b = b / 255;
            
            const max = Math.max(r, g, b);
            const min = Math.min(r, g, b);
            let h, s, l = (max + min) / 2;
            
            if (max === min) {
                h = s = 0;
            } else {
                const d = max - min;
                s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                
                switch (max) {
                    case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                    case g: h = ((b - r) / d + 2) / 6; break;
                    case b: h = ((r - g) / d + 4) / 6; break;
                }
            }
            
            return { h: h * 360, s: s * 100, l: l * 100 };
        },
        hslToRgb(h, s, l) {
            h = h / 360;
            s = s / 100;
            l = l / 100;
            
            let r, g, b;
            
            if (s === 0) {
                r = g = b = l;
            } else {
                const hue2rgb = (p, q, t) => {
                    if (t < 0) t += 1;
                    if (t > 1) t -= 1;
                    if (t < 1/6) return p + (q - p) * 6 * t;
                    if (t < 1/2) return q;
                    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
                    return p;
                };
                
                const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
                const p = 2 * l - q;
                
                r = hue2rgb(p, q, h + 1/3);
                g = hue2rgb(p, q, h);
                b = hue2rgb(p, q, h - 1/3);
            }
            
            return {
                r: Math.round(r * 255),
                g: Math.round(g * 255),
                b: Math.round(b * 255)
            };
        },
        rgbToHex(r, g, b) {
            return '#' + [r, g, b].map(x => {
                const hex = x.toString(16);
                return hex.length === 1 ? '0' + hex : hex;
            }).join('');
        },
        adjustColorLightness(hex, lightnessAdjustment) {
            const rgb = this.hexToRgb(hex);
            if (!rgb) return hex;
            
            const hsl = this.rgbToHsl(rgb.r, rgb.g, rgb.b);
            hsl.l = Math.max(0, Math.min(100, hsl.l + lightnessAdjustment));
            
            const adjustedRgb = this.hslToRgb(hsl.h, hsl.s, hsl.l);
            return this.rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
        },
        getContrastTextColor(hex) {
            // Returns '#FFFFFF' or '#000000' depending on which gives a more
            // readable contrast against the supplied background color (WCAG).
            const rgb = this.hexToRgb(hex);
            if (!rgb) return '#FFFFFF';

            const toLinear = channel => {
                const c = channel / 255;
                return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
            };
            const luminance = 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);

            const contrastWithWhite = 1.05 / (luminance + 0.05);
            const contrastWithBlack = (luminance + 0.05) / 0.05;
            return contrastWithWhite >= contrastWithBlack ? '#FFFFFF' : '#000000';
        },
        applyBrandColor(brandColor) {
            if (brandColor) {
                // Set base color
                document.documentElement.style.setProperty('--brand-primary-color', brandColor);
                
                // Calculate and set darker variant (20% darker)
                const darkerColor = this.adjustColorLightness(brandColor, -20);
                document.documentElement.style.setProperty('--brand-primary-dark', darkerColor);
                
                // Calculate and set light variant (15% lighter - keeps contrast for white text)
                const lightColor = this.adjustColorLightness(brandColor, 15);
                document.documentElement.style.setProperty('--brand-primary-light', lightColor);
                
                // Calculate and set lighter variant (70% lighter for subtle backgrounds)
                const lighterColor = this.adjustColorLightness(brandColor, 70);
                document.documentElement.style.setProperty('--brand-primary-lighter', lighterColor);
                
                // Choose readable font color (white or black) for text on the brand color
                const textColor = this.getContrastTextColor(brandColor);
                document.documentElement.style.setProperty('--brand-primary-text-color', textColor);
                
                // Update Vuetify theme if available
                if (this.$vuetify && this.$vuetify.theme && this.$vuetify.theme.themes && this.$vuetify.theme.themes.light) {
                    this.$vuetify.theme.themes.light.primary = brandColor;
                }
            }
        }
    },
    beforeCreate() {
        EventBus.$on('authState', info => {
            console.log("Checking Auth State on App: " + JSON.stringify(info));
            if (info && info.status === 'needAuthentication') {
                //Some flow has thrown a request for authentication.
                //Verify the session is actually gone before bouncing to the login
                //page. Transient failures (e.g. Safari suspending requests during
                //pinch/tab gestures) can emit this while the user is still signed in.
                Auth.currentAuthenticatedUser()
                    .then(user => {
                        if (user && user.signInUserSession && user.signInUserSession.isValid()) {
                            console.log("Ignoring needAuthentication event: session is still valid.");
                        } else {
                            this.signIn(decodeURIComponent(info.redirect), info.reason);
                        }
                    })
                    .catch(() => {
                        this.signIn(decodeURIComponent(info.redirect), info.reason);
                    });
            } else if (info && info.status === 'signedIn') {
                //Sign in flow completed.
                this.isAuthenticated = true;

                let queryParams = [];
                if (info.plan) {
                    queryParams.push("plan=" + info.plan);
                }
                if (info.acceptVaultId) {
                    queryParams.push("accept=" + info.acceptVaultId);
                }

                if (this.$route.query.embedded) {
                	queryParams.push("embedded=" + this.$route.query.embedded);
                }

                if (info.redirect) {
                    let paramMarker = info.redirect.indexOf('?') > -1 ? "&" : "?";
                    this.$router.push(info.redirect + (queryParams.length > 0 ? (paramMarker + queryParams.join("&")) : ""));
                } else {
                    this.$router.push("/vaults" + (queryParams.length > 0 ? ("?" + queryParams.join("&")) : ""));
                }
            } else if (info && info.status === 'signedOut') {
                //Proactive logout request from the profile menu.
                this.user = null;
                this.cognito = null;
                this.organization = null;
                this.isAuthenticated = false;

                this.signIn("/vaults", "You have been successfully signed out.");
            }
        });
    },
    computed: {
        layout() {
            if (this.$route.path === '/') {
                return '';
            } else {
                return this.$route.meta.requiresAuth ? 'base-auth' : 'base-unauth';
            }
        }
    }
});
</script>

<style lang="scss">
@import 'assets/scss/style';
</style>
